/**
 * Whether a question asks for its answer to be emailed, and what the Email
 * control under the reply should then carry.
 *
 * The analyst cannot send mail; the Email control builds an Outlook draft in
 * the browser. It is offered only when the user asked for it, and the agent's
 * own wording about emailing is removed from the reply because the control
 * already says what it does.
 */

const EMAIL = String.raw`e-?mail|mail`;
// "by email domain" or "share of email opt-ins" is about an email column, not a request.
const NOT_A_FIELD = String.raw`(?!s?\s+(address|domain|id|column|field|type|status|opt|campaign|open|click|bounce)s?\b)`;

const REQUEST_PATTERNS: RegExp[] = [
  // "send an email", "send this by mail", "share it via email", "forward ... email"
  new RegExp(String.raw`\b(send|forward|share)\b[^.?!\n]{0,40}\b(${EMAIL})\b${NOT_A_FIELD}`, "i"),
  // "email this to", "mail the report to", "email it over to"
  new RegExp(String.raw`\b(${EMAIL})\s+(this|it|that|these|them|the\b[^.?!\n]{0,40}?)\s*(over\s+)?to\b`, "i"),
  // "email me", "email it", "mail this"
  new RegExp(String.raw`\b(${EMAIL})\s+(me|us|him|her|them|it|this|that|these|my\s+\w+)\b`, "i"),
  // "draft an email", "write a mail"
  new RegExp(String.raw`\b(draft|write|compose|prepare)\b[^.?!\n]{0,20}\b(${EMAIL})\b`, "i"),
  // "by email", "via e-mail", "in an email"
  new RegExp(String.raw`\b(by|via|over|through|in\s+an?)\s+(${EMAIL})\b${NOT_A_FIELD}`, "i"),
];

/** True when the user's message asks for the answer to be emailed. */
export function isEmailRequest(question: string | undefined | null): boolean {
  const text = question?.trim();
  if (!text) return false;
  return REQUEST_PATTERNS.some((pattern) => pattern.test(text));
}

const DISCLAIMER_PATTERNS: RegExp[] = [
  /\bemail button\b/i,
  new RegExp(
    String.raw`\b(can(no|['’])?t|cannot|unable to|not able to|don['’]t have the ability to)\b[^.!?\n]{0,60}\b(${EMAIL}|send)`,
    "i",
  ),
  /\boutlook\b[^.!?\n]*\b(draft|recipients?|press send)\b/i,
  /\byou['’]ll choose the recipients\b/i,
];

/** The reply the agent sends when a message only asks for an email. */
const READY_REPLY = /^ready to e-?mail\.?$/i;

function isDisclaimer(sentence: string): boolean {
  return DISCLAIMER_PATTERNS.some((pattern) => pattern.test(sentence));
}

/** Removes sentences that explain the analyst cannot email; other lines are kept verbatim. */
export function stripEmailDisclaimer(text: string): string {
  const lines = text.split("\n").flatMap((line) => {
    if (!line.trim() || !isDisclaimer(line)) return [line];
    const kept = line.split(/(?<=[.!?])\s+/).filter((sentence) => !isDisclaimer(sentence));
    const rest = kept.join(" ");
    return rest.replace(/^[\s>*\-+\d.)]*$/, "").trim() ? [rest] : [];
  });
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export interface PlannedMessage {
  id: string;
  role: string;
  content: string;
  /** Replies that are cards, dashboards or documents are not text answers. */
  plain: boolean;
}

export interface EmailPlan {
  /** What to show for the reply. */
  text: string;
  /** What the Email control carries, or null when it is not offered. */
  email: { answer: string; question?: string } | null;
}

/**
 * For each plain assistant reply: the text to show and, when the question
 * before it asked for an email, the answer to put in the draft. A message that
 * only asks to email ("email this to Finance") carries the previous answer.
 */
export function planEmails(messages: PlannedMessage[]): Map<string, EmailPlan> {
  const plans = new Map<string, EmailPlan>();
  let question: string | undefined;
  let lastAnswer: { answer: string; question?: string } | null = null;

  for (const message of messages) {
    if (message.role === "user") {
      if (message.content.trim()) question = message.content;
      continue;
    }
    if (message.role !== "assistant" || !message.plain) continue;

    if (!isEmailRequest(question)) {
      plans.set(message.id, { text: message.content, email: null });
      if (message.content.trim()) lastAnswer = { answer: message.content, question };
      continue;
    }

    const text = stripEmailDisclaimer(message.content);
    if (text && !READY_REPLY.test(text)) {
      const own = { answer: text, question };
      plans.set(message.id, { text, email: own });
      lastAnswer = own;
    } else if (lastAnswer) {
      plans.set(message.id, { text: "Ready to email the previous answer.", email: lastAnswer });
    } else {
      plans.set(message.id, { text: "There is no answer to email yet. Ask a question first.",
        email: null });
    }
  }
  return plans;
}
